import { isBrowser } from './detection';

/**
 * Calculates the offset needed for scrolling (e.g., fixed header height).
 * Checks for common fixed/sticky elements at the top of the page.
 *
 * @returns The offset in pixels to subtract from scroll position
 */
function getScrollOffset(): number {
  // Check for fixed/sticky header or other top elements
  const header = document.querySelector('header');

  if (!header) return 0;

  const headerStyle = window.getComputedStyle(header);
  const isFixed = headerStyle.position === 'fixed' || headerStyle.position === 'sticky';

  if (isFixed) {
    return header.offsetHeight;
  }

  return 0;
}

/**
 * Scrolls to an element with the given ID using smooth scrolling.
 * Optionally updates the URL hash without triggering a page reload.
 *
 * @param elementId - The ID of the element to scroll to
 * @param addUrlHash - Whether to add the element ID to the URL hash (default: true)
 * @returns true if the element was found and scrolled to, false otherwise
 */
export function scrollToElementIntoView(elementId: string, addUrlHash = true): boolean {
  if (!isBrowser()) return false;

  const element = document.getElementById(elementId);

  if (!element) {
    return false;
  }

  // Update URL hash first (before scrolling) if requested
  if (addUrlHash) {
    const newHash = `#${elementId}`;

    if (window.location.hash !== newHash) {
      window.history.pushState(null, '', newHash);
    }
  }

  // Then scroll to the element
  element.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return true;
}

/**
 * Handles hash scrolling on initial page load.
 *
 * Waits for window 'load' event (all resources including images loaded),
 * then smoothly scrolls to the hash target element with proper offset calculation.
 *
 * Performance considerations:
 * - Uses window 'load' event (standard approach for image-heavy pages)
 * - Uses requestAnimationFrame (no setTimeout) for optimal timing
 * - Accounts for fixed/sticky headers
 * - Doesn't block main thread
 * - Doesn't interfere with Core Web Vitals (CLS, LCP, INP)
 * - Works on both desktop and mobile
 *
 * @example
 * // In a client component
 * useEffect(() => {
 *   handleInitialHashScroll();
 * }, []);
 */
export function handleInitialHashScroll(): void {
  if (!isBrowser()) return;

  // Check if URL has a hash
  const hash = window.location.hash;

  if (!hash) return;

  // Extract element ID (remove the # prefix)
  const elementId = hash.slice(1);

  if (!elementId) return;

  // Function to perform the scroll
  const performScroll = () => {
    const element = document.getElementById(elementId);

    if (!element) return;

    requestAnimationFrame(() => {
      // Get element's position relative to the document
      const elementRect = element.getBoundingClientRect();
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const elementPosition = elementRect.top + scrollTop;

      // Calculate offset for fixed/sticky headers
      const offset = getScrollOffset();

      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth',
      });
    });
  };

  // Check document ready state
  if (document.readyState === 'complete') {
    // All resources already loaded - scroll immediately
    performScroll();
  } else {
    // Wait for all resources (images, styles, etc.) to load
    // This prevents layout shifts from images loading above the hash element
    window.addEventListener('load', performScroll, { once: true });
  }
}
